import { useQuery } from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';
import { OLDEST_FIRST } from 'src/lib/pickerOrder';
import type { RecipientPickerQuery, RecipientPickerQueryVariables } from 'types/graphql';

import { RECIPIENT_PICKER_QUERY } from '../api/sendEmail.api';

type ContactEdge = NonNullable<NonNullable<RecipientPickerQuery['contactPage']['edges']>[number]>;
type MembershipEdge = NonNullable<NonNullable<RecipientPickerQuery['membershipPage']['edges']>[number]>;
export type RecipientContact = ContactEdge['node'];
export type RecipientMembership = MembershipEdge['node'];

/** First page size per list; a recipient beyond it is reached by typing (server search on name and email). */
export const RECIPIENT_PAGE_SIZE = 50;
export const RECIPIENT_SEARCH_DEBOUNCE_MS = 250;

const NO_CONTACTS: RecipientContact[] = [];
const NO_MEMBERSHIPS: RecipientMembership[] = [];
const BY_NAME = [{ field: 'name', sort: 'asc' }];

const nodesOf = <Node>(edges: ReadonlyArray<{ node: Node } | null> | null | undefined) =>
  edges?.flatMap((edge) => (edge ? [edge.node] : []));

/**
 * The To/CC options: contacts (optionally one business entity's) + the organization's members. The
 * first page of each loads once the modal opens (members oldest first, like the unordered `memberships` list;
 * contacts by name), then a debounced server search runs as the user types.
 */
export function useRecipients({ businessEntityId, open }: { businessEntityId?: number; open: boolean }) {
  const [typed, setTyped] = useState('');
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setSearchText(typed.trim()), RECIPIENT_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [typed]);

  useEffect(() => {
    if (open) return;
    setTyped('');
    setSearchText('');
  }, [open]);

  const search = searchText ? { searchText } : {};
  const { data, previousData, loading, error } = useQuery<RecipientPickerQuery, RecipientPickerQueryVariables>(
    RECIPIENT_PICKER_QUERY,
    {
      skip: !open,
      variables: {
        businessEntityId,
        contactInput: { first: RECIPIENT_PAGE_SIZE, sortItems: BY_NAME, ...search },
        membershipInput: { first: RECIPIENT_PAGE_SIZE, sortItems: OLDEST_FIRST, ...search },
      },
    }
  );

  const shown = data ?? previousData;
  const contacts = useMemo(() => nodesOf(shown?.contactPage.edges) ?? NO_CONTACTS, [shown]);
  const memberships = useMemo(() => nodesOf(shown?.membershipPage.edges) ?? NO_MEMBERSHIPS, [shown]);

  return { contacts, memberships, loading, error, search: setTyped };
}
